import styled from "@emotion/styled";
import theme from "../../styles/theme";
import { keyframes } from "@emotion/react";

type HeaderLinkProps = {
  isOpen?: boolean;
  isDarkMode: boolean;
  transitionPercent: number;
  offsetTop: number;
  offsetTopMainHeader: number;
};

interface IHeaderLinksBaseProps {
  isOpen: boolean;
  showOverlay: boolean;
  showAnnouncementBanner: boolean;
  isDarkMode?: boolean;
  offsetTop: number;
}

interface IDrawerItemsProps {
  isOpen: boolean;
  isOpenMobile: boolean;
}

export const HeaderLinksBase = styled.div<IHeaderLinksBaseProps>`
  ${theme.smallerThan.lg} {
    position: fixed;
    left: 0;
    right: 0;
    top: 0;
    bottom: 0;
    background-color: ${theme.colors.white};
    overflow-y: ${(props) => (props.isOpen ? "auto" : "hidden")};
    overflow-x: hidden;
    height: ${(props) => (props.isOpen ? "100vh" : "0px")};
    opacity: ${(props) => (props.isOpen ? "1" : "0")};
    transition: all 0.1s ease-out;
  }

  &:after {
    content: "";
    display: block;
    position: fixed;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    transform: translateY(
      ${(props) => (props.showOverlay ? `${props.offsetTop}px` : "-120%")}
    );
    transition: transform 0.1s ease-out
      ${(props) => (props.showOverlay ? "0" : "2s")};
    backdrop-filter: blur(6px);
    animation: ${(props) => (props.showOverlay ? "fade-in" : "fade-out")} 0.25s
      ease-in-out forwards;
    z-index: 0;
    height: 100vh;

    ${theme.smallerThan.lg} {
      display: none;
    }
  }

  @keyframes fade-out {
    0% {
      opacity: 100%;
    }
    100% {
      opacity: 0%;
    }
  }
  @keyframes fade-in {
    0% {
      opacity: 0%;
    }
    100% {
      opacity: 100%;
    }
  }
`;

export const LinksContainer = styled.ul<IDrawerItemsProps>`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[8]};
  margin: 0;
  padding: 0;
  list-style: none;
  transition: all 0.3s ease-out;

  ${theme.smallerThan.lg} {
    padding: 80px 0;
    opacity: ${(props) => (props.isOpenMobile ? "1" : "0")};
    z-index: ${(props) => (props.isOpenMobile ? "1" : "-1")};
    height: ${(props) => props.isOpenMobile && "100%"};
    transform: translateY(${(props) => (props.isOpenMobile ? "0" : "-20%")});
    transition: all 0.5s ease-out;
    box-sizing: border-box;

    > li {
      > span {
        display: inline-block;
        opacity: ${(props) => (props.isOpen ? "0" : "1")};
        transform: translateX(${(props) => (props.isOpen ? "-20px" : "0")});
        transition: all 0.3s ease-out;
      }
    }
  }

  ${theme.largerThan.lg} {
    flex-direction: row;
    align-items: center;
  }
`;

export const DrawerTitleLink = styled.li<HeaderLinkProps>`
  white-space: nowrap;
  text-decoration: none;
  outline: none;
  cursor: pointer;
  color: ${theme.colors.darkBlues[100]};

  ${theme.largerThan.lg} {
    transition: color 0.5s ease-out;
    color: ${(props) =>
      props.isDarkMode && props.transitionPercent > 0
        ? theme.colors.white
        : theme.colors.darkBlues[100]};
  }

  > span {
    font-size: ${theme.fontSizes[2]};
    cursor: pointer;
    position: relative;

    ${theme.largerThan.lg} {
      font-weight: ${theme.fontWeights.semibold};
    }

    ${theme.smallerThan.lg} {
      font-size: ${theme.fontSizes[4]};
      opacity: ${(props) => (props.isOpen ? "0" : "1")};
      transition: all 0.3s ease-out;
    }
  }

  ${theme.smallerThan.lg} {
    > span {
      padding: 0 ${theme.spacing[4]};
    }
  }

  > span,
  > i {
    ${(props) => (props.isOpen ? `color: ${theme.colors.blues[100]};` : ``)}
  }

  &:hover {
    > span,
    > i {
      color: ${theme.colors.blues[100]};
    }
  }

  &:first-of-type {
    padding-left: 0px;
  }

  ${theme.smallerThan.lg} {
    font-size: ${theme.fontSizes[4]};
  }

  .drawer {
    height: 0;
    padding: 0;
    overflow: hidden;

    ${theme.smallerThan.lg} {
      position: fixed;
      z-index: -1;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      height: auto;
      font-size: ${theme.fontSizes[2]};
      transform: translateX(20px);
      opacity: 0;
      transition: all 0.3s ease-out;
      padding: 1.5rem ${theme.spacing[4]};
      overflow-y: scroll;
    }

    ${theme.largerThan.lg} {
      position: absolute;
      z-index: 0;
      top: ${(props) => props.offsetTopMainHeader}px;
      left: 0;
      right: 0;
    }

    &.open {
      height: auto;
      padding-bottom: 150px; // fix overflow issue
      z-index: 10;
      background-color: ${theme.colors.white};

      ${theme.smallerThan.lg} {
        opacity: 1;
        transform: translateX(0);
      }

      ${theme.largerThan.lg} {
        border-top: 1px solid ${theme.colors.grays[6]};
        border-bottom: 1px solid ${theme.colors.grays[6]};
        box-shadow: 0 5px 5px 0 rgba(0, 0, 0, 0.1);
        padding: ${theme.spacing[8]};
      }

      > ul {
        opacity: 1;
        max-height: none;

        ${theme.largerThan.lg} {
          max-height: 600px;

          @media (min-height: 900px) {
            max-height: 700px;
          }

          > li {
            width: 22.33%;
          }

          // Styling specific to the default links drawer
          // Could use some refactoring to make it more generic or set via CMS
          &.default-links {
            &.drawer-1 {
              > li {
                width: 75%;
              }
            }

            &.drawer-2 {
              max-height: 400px;
            }

            &.drawer-4 {
              flex-direction: row;
              max-height: 300px;
            }
          }
        }
      }
    }

    ${theme.largerThan.xl} {
      .back {
        display: none;
      }
    }

    > ul {
      max-width: ${theme.maxWidth};
      margin: 0 auto;
      padding: 0;
      list-style: none;
      position: relative;
      display: flex;
      flex-direction: column;
      flex-wrap: wrap;
      justify-content: flex-start;
      overflow: hidden;
      max-height: 0;
      gap: ${(props) => (props.isOpen ? theme.spacing[8] : theme.spacing[2])};
      opacity: 0;
      transition: opacity 0.5s ease-out;

      ${theme.smallerThan.lg} {
        margin-top: 36px;
        gap: ${theme.spacing[8]};
      }

      > li {
        ${theme.largerThan.lg} {
          width: 25%;
        }

        > span {
          display: block;
          margin-bottom: 1.5rem;
          font-size: ${theme.fontSizes[0]};
          color: ${theme.colors.darkBlues[50]};
          text-transform: uppercase;
          letter-spacing: 0.1em;
          cursor: pointer;

          &:hover {
            color: ${theme.colors.blues[100]};
          }
        }

        ${theme.largerThan.lg} {
          &.open {
            > span {
              color: ${theme.colors.blues[100]};
            }
          }
        }

        ul {
          margin: 0;
          padding: 0;
          list-style: none;

          li {
            margin-bottom: ${theme.spacing[2]};

            @media (min-height: 900px) {
              margin-bottom: ${theme.spacing[4]};
            }
          }
        }
      }
    }
  }
`;

export const HeaderLinkIcon = styled.i`
  ${theme.smallerThan.lg} {
    display: none;
  }

  ${theme.largerThan.lg} {
    display: inline-block;
    margin-left: ${theme.spacing[1]};
    font-size: 0.5rem;
  }
`;

export const HeaderLinkIconMobile = styled.i`
  margin-left: ${theme.spacing[4]};
  font-size: 1rem;
  vertical-align: 0 !important;

  ${theme.largerThan.lg} {
    display: none;
  }
`;

export const PageLink = styled.a<HeaderLinkProps>`
  white-space: nowrap;
  text-decoration: none;
  font-size: ${theme.fontSizes[2]};
  position: relative;

  &:hover {
    color: ${theme.colors.blues[100]};
  }

  ${theme.smallerThan.lg} {
    padding: 0 ${theme.spacing[4]};
  }

  ${theme.largerThan.lg} {
    font-weight: ${theme.fontWeights.semibold};
    color: ${(props) =>
      props.isDarkMode && props.transitionPercent > 0
        ? theme.colors.white
        : theme.colors.darkBlues[100]};
  }

  ${theme.smallerThan.lg} {
    display: block;
    color: ${theme.colors.darkBlues[100]};
    font-size: ${theme.fontSizes[4]};
    transform: translateX(${(props) => (props.isOpen ? "-20px" : "0px")});
    transition: all 0.3s ease-out;
  }
`;

export const DrawerLink = styled.div`
  & a {
    font-size: ${theme.fontSizes[3]};
    font-weight: ${theme.fontWeights.regular};
    color: ${theme.colors.darkBlues[100]};
    white-space: normal;

    &:hover {
      color: ${theme.colors.blues[100]};
    }

    ${theme.largerThan.lg} {
      font-size: ${theme.fontSizes[2]};
      font-weight: ${theme.fontWeights.semibold};
    }
  }
`;

export const BackButton = styled.span`
  display: block;
  line-height: 1;

  ${theme.largerThan.lg} {
    display: none;
  }
`;
